import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, Fade, Paper, Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, makeStyles } from "@material-ui/core";
import { Close } from "@material-ui/icons";

import { getConversionOverview } from "../../../../components/common/ApiMiddleware";
import { addSnackbarIssue } from "../../../../components/common/StoreCommon";
import { getSelectedTimePeriodText } from "../../../../store/ui/conversion";
import { convertDateTime } from "../../../../components/common/Common";

export default function ConversionDetailModal({ open, onClose, metricName, userId, conversionMetricId }) {
  const classes = useStyles();
  const dateTimePeriod = useSelector((state) => getSelectedTimePeriodText(state.ui.conversion));
  const [overviewData, setOverviewData] = useState(null);
  const rows = [...(Array.isArray(overviewData) ? overviewData : [])].sort(
    (a, b) => new Date(b.TxnDateTime) - new Date(a.TxnDateTime)
  );
  const hideValueColumn = rows.length > 0 && rows.every((item) => String(item.Value ?? "") === "1");

  useEffect(() => {
    if (open && userId && dateTimePeriod != null && conversionMetricId != null) {
      setOverviewData(null);
      getConversionOverview(userId, dateTimePeriod, conversionMetricId)
        .then((data) => setOverviewData(data))
        .catch(() => {
          setOverviewData(null);
          addSnackbarIssue("Unable to fetch conversion information", "warning");
          onClose();
        });
    }
  }, [open, userId, dateTimePeriod, conversionMetricId, onClose]);

  return (
    <Modal
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      open={open}
      onClose={onClose}
      BackdropProps={{
        timeout: 500,
        style: { background: "rgba(50,50,50,0.5)" },
      }}
      aria-labelledby="Conversion detail"
      aria-describedby="Conversion metric details"
    >
      <Fade in={open}>
        <Paper className={classes.paper}>
          <Box className={classes.content}>
            <IconButton className={classes.closeButton} size="small" onClick={onClose} aria-label="close">
              <Close />
            </IconButton>
            {overviewData === null ? (
              <Box className={classes.loading}>
                <CircularProgress size={40} />
              </Box>
            ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>Date Time</TableCell>
                    <TableCell className={classes.tableHeader}>Reference</TableCell>
                    {!hideValueColumn && <TableCell className={classes.tableHeader}>Value</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((item, index) => {
                    const dateTime = item.TxnDateTime;
                    const reference = item.DisplayText ?? "";
                    const value = item.Value ?? "";
                    const formattedDateTime = dateTime ? convertDateTime(dateTime, true) : "";
                    return (
                      <TableRow key={index}>
                        <TableCell>{formattedDateTime}</TableCell>
                        <TableCell>{reference}</TableCell>
                        {!hideValueColumn && <TableCell>{value}</TableCell>}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
}

const useStyles = makeStyles((theme) => ({
  paper: {
    minWidth: 320,
    maxWidth: "90vw",
    maxHeight: "80vh",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    borderRadius: 10,
    position: "relative",
  },
  content: {
    overflow: "auto",
    minHeight: 120,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  tableHeader: {
    fontWeight: "bold",
  },
}));
